import React, { useState, useEffect } from 'react';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import Avatar from '@material-ui/core/Avatar';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { makeStyles } from '@material-ui/core/styles';
import PageTitle from '../../Utils/smsTitle';
import { SubHeader } from '../../../Containers/SubHeader';
import { DoctorTypeBadge } from '../shared';
import { DOCTOR_TYPE, FEE_MODEL } from '../shared/constants';
import { surgeryService } from '../../../services/surgeryService';
import { history } from '../../../Helpers/history';


const FEE_MODEL_LABELS = {
  [FEE_MODEL.PERCENTAGE_SPLIT]: 'Percentage Split',
  [FEE_MODEL.FLAT_RATE]: 'Flat Rate',
  [FEE_MODEL.DIRECT_BILLING]: 'Direct Billing',
};

const getInitials = (name) => {
  if (!name) return '?';
  return name
    .replace(/^Dr\.\s*/i, '')
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const isExternalDoctor = (type) =>
  type === DOCTOR_TYPE.VISITING_CONSULTANT ||
  type === DOCTOR_TYPE.LOCUM ||
  type === DOCTOR_TYPE.REFERRED_SPECIALIST;

// ─── Styles ────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  root: {
    margin: 22,
  },
  backBtn: {
    textTransform: 'none',
    marginBottom: 16,
    fontWeight: 500,
  },
  topCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 20,
    padding: '24px 28px',
    marginBottom: 16,
    borderRadius: 8,
  },
  avatar: {
    width: 72,
    height: 72,
    fontSize: '1.5rem',
    fontWeight: 600,
    backgroundColor: '#1976d2',
  },
  doctorInfo: {
    flex: 1,
  },
  doctorName: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#222',
    marginBottom: 4,
  },
  specialty: {
    fontSize: '0.95rem',
    color: '#555',
    marginBottom: 8,
  },
  badgeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  statusDot: {
    display: 'inline-block',
    width: 10,
    height: 10,
    borderRadius: '50%',
    marginRight: 6,
    verticalAlign: 'middle',
  },
  statusText: {
    fontSize: '0.85rem',
    fontWeight: 500,
    verticalAlign: 'middle',
  },
  sectionPaper: {
    padding: '20px 24px',
    marginBottom: 16,
    borderRadius: 8,
  },
  sectionTitle: {
    fontSize: '1.05rem',
    fontWeight: 600,
    color: '#333',
    marginBottom: 12,
  },
  detailRow: {
    display: 'flex',
    padding: '8px 0',
  },
  detailLabel: {
    width: 180,
    fontSize: '0.85rem',
    color: '#777',
    fontWeight: 500,
    flexShrink: 0,
  },
  detailValue: {
    fontSize: '0.85rem',
    color: '#333',
    flex: 1,
  },
  certList: {
    paddingLeft: 18,
    margin: 0,
  },
  certItem: {
    fontSize: '0.85rem',
    color: '#333',
    lineHeight: 1.6,
  },
  verifiedChip: {
    fontWeight: 500,
    fontSize: '0.75rem',
  },
  privilegeChip: {
    marginRight: 6,
    marginBottom: 4,
    height: 26,
    fontSize: '0.78rem',
    fontWeight: 500,
  },
  loadingWrapper: {
    display: 'flex',
    justifyContent: 'center',
    padding: 80,
  },
  notFound: {
    padding: 60,
    textAlign: 'center',
    color: '#999',
  },
}));

// ─── Component ─────────────────────────────────────────────────────────────

const DoctorProfileCard = (props) => {
  const classes = useStyles();
  const { doctorId } = props.params;


  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await surgeryService.searchDoctors();
        const data = res?.data || res;
        const found = (data.doctors || []).find((d) => d.id === doctorId);
        if (!cancelled) setDoctor(found || null);
      } catch (err) {
        console.error('Failed to load doctor:', err);
        if (!cancelled) setDoctor(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [doctorId]);

  if (loading) {
    return (
      <div>
        <PageTitle title="Doctor Profile" />
        <SubHeader title="Surgery" subTitle="Doctor Profile" />
        <div className={classes.loadingWrapper}>
          <CircularProgress size={32} />
        </div>
      </div>
    );
  }

  if (!doctor) {
    return (
      <div>
        <PageTitle title="Doctor Profile" />
        <SubHeader title="Surgery" subTitle="Doctor Profile" />
        <div className={classes.root}>
          <Button
            startIcon={<ArrowBackIcon />}
            className={classes.backBtn}
            onClick={() => history.push('/surgery/doctors')}
          >
            Back to Registry
          </Button>
          <Paper elevation={1} className={classes.notFound}>
            <Typography variant="h6">Doctor not found</Typography>
            <Typography variant="body2" style={{ marginTop: 4 }}>
              The requested doctor profile does not exist.
            </Typography>
          </Paper>
        </div>
      </div>
    );
  }

  const profile = {};
  const credentials = profile.credentials || null;
  const privileges = profile.privileges || null;
  const feeAgreement = profile.feeAgreement || null;
  const showFees = isExternalDoctor(doctor.type) && feeAgreement;

  return (
    <div>
      <PageTitle title={`${doctor.name} - Profile`} />
      <SubHeader title="Surgery" subTitle="Doctor Profile" />

      <div className={classes.root}>
        <Button
          startIcon={<ArrowBackIcon />}
          className={classes.backBtn}
          onClick={() => history.push('/surgery/doctors')}
        >
          Back to Registry
        </Button>

        {/* Top Card */}
        <Paper elevation={1} className={classes.topCard}>
          <Avatar className={classes.avatar}>{getInitials(doctor.name)}</Avatar>
          <div className={classes.doctorInfo}>
            <Typography className={classes.doctorName}>{doctor.name}</Typography>
            <Typography className={classes.specialty}>{doctor.speciality}</Typography>
            <div className={classes.badgeRow}>
              <DoctorTypeBadge doctorType={doctor.type} />
              <span>
                <span
                  className={classes.statusDot}
                  style={{ backgroundColor: doctor.available ? '#4CAF50' : '#F44336' }}
                />
                <span
                  className={classes.statusText}
                  style={{ color: doctor.available ? '#4CAF50' : '#F44336' }}
                >
                  {doctor.available ? 'Available' : 'Unavailable'}
                </span>
              </span>
            </div>
          </div>
        </Paper>

        {/* Credentials Section */}
        <Paper elevation={1} className={classes.sectionPaper}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
              Credentials
            </Typography>
            <Chip
              label={credentials.verified ? 'Verified' : 'Pending Verification'}
              size="small"
              className={classes.verifiedChip}
              style={{
                backgroundColor: credentials.verified ? '#E8F5E9' : '#FFF8E1',
                color: credentials.verified ? '#2E7D32' : '#F57F17',
                borderColor: credentials.verified ? '#4CAF50' : '#FFC107',
              }}
              variant="outlined"
            />
          </div>

          <div className={classes.detailRow}>
            <Typography className={classes.detailLabel}>License Number</Typography>
            <Typography className={classes.detailValue}>{credentials.licenseNumber}</Typography>
          </div>
          <div className={classes.detailRow}>
            <Typography className={classes.detailLabel}>Issuing Authority</Typography>
            <Typography className={classes.detailValue}>{credentials.issuingAuthority}</Typography>
          </div>
          <div className={classes.detailRow}>
            <Typography className={classes.detailLabel}>License Expiry</Typography>
            <Typography className={classes.detailValue}>{credentials.expiryDate}</Typography>
          </div>
          <div className={classes.detailRow}>
            <Typography className={classes.detailLabel}>Board Certifications</Typography>
            <div className={classes.detailValue}>
              <ul className={classes.certList}>
                {credentials.certifications.map((cert, idx) => (
                  <li key={idx} className={classes.certItem}>{cert}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className={classes.detailRow}>
            <Typography className={classes.detailLabel}>Specialization</Typography>
            <Typography className={classes.detailValue}>{credentials.specialization}</Typography>
          </div>
        </Paper>

        {/* Surgical Privileges Section */}
        <Paper elevation={1} className={classes.sectionPaper}>
          <Typography className={classes.sectionTitle}>Surgical Privileges</Typography>

          <div className={classes.detailRow}>
            <Typography className={classes.detailLabel}>Granted Privileges</Typography>
            <div className={classes.detailValue}>
              {privileges.granted.map((p) => (
                <Chip
                  key={p}
                  label={p}
                  size="small"
                  className={classes.privilegeChip}
                  style={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }}
                />
              ))}
            </div>
          </div>

          {privileges.restricted.length > 0 && (
            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Restricted Procedures</Typography>
              <div className={classes.detailValue}>
                {privileges.restricted.map((p) => (
                  <Chip
                    key={p}
                    label={p}
                    size="small"
                    className={classes.privilegeChip}
                    style={{ backgroundColor: '#FFEBEE', color: '#C62828' }}
                  />
                ))}
              </div>
            </div>
          )}

          <div className={classes.detailRow}>
            <Typography className={classes.detailLabel}>Privilege Expiry</Typography>
            <Typography className={classes.detailValue}>{privileges.expiryDate}</Typography>
          </div>
        </Paper>

        {/* Fee Agreements Section (external doctors only) */}
        {showFees && (
          <Paper elevation={1} className={classes.sectionPaper}>
            <Typography className={classes.sectionTitle}>Fee Agreement</Typography>

            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Fee Model</Typography>
              <Typography className={classes.detailValue}>
                {FEE_MODEL_LABELS[feeAgreement.model] || feeAgreement.model}
              </Typography>
            </div>
            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Rate Details</Typography>
              <Typography className={classes.detailValue}>{feeAgreement.rate}</Typography>
            </div>
            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Agreement Period</Typography>
              <Typography className={classes.detailValue}>
                {feeAgreement.periodStart} &mdash; {feeAgreement.periodEnd}
              </Typography>
            </div>
            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Status</Typography>
              <Chip
                label={feeAgreement.status === 'active' ? 'Active' : 'Expired'}
                size="small"
                className={classes.verifiedChip}
                style={{
                  backgroundColor: feeAgreement.status === 'active' ? '#E8F5E9' : '#FFEBEE',
                  color: feeAgreement.status === 'active' ? '#2E7D32' : '#C62828',
                  borderColor: feeAgreement.status === 'active' ? '#4CAF50' : '#F44336',
                }}
                variant="outlined"
              />
            </div>
          </Paper>
        )}
      </div>
    </div>
  );
};

export default DoctorProfileCard;
